import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Save, Settings2 } from "lucide-react";
import { fetchConfig, saveConfig } from "@/lib/queries";
import { dateTime } from "@/lib/format";
import type { AppConfigRow } from "@/lib/types";

function ConfigRow({ row }: { row: AppConfigRow }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState(JSON.stringify(row.value, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (value: unknown) => saveConfig(row.key, value),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["config"] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: Error) => setError(err.message),
  });

  function submit() {
    try {
      const parsed = JSON.parse(text);
      mutation.mutate(parsed);
    } catch {
      setError("Not valid JSON");
    }
  }

  const risky = row.key.includes("cap") || row.key.includes("enabled") || row.key.includes("min_supported");

  return (
    <div className="config-row">
      <div className="row-between">
        <div>
          <div className="key">{row.key}</div>
          <div className="tiny faint">{row.description ?? "No description"}</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {risky && (
            <span className="badge badge-sunshine">
              <AlertTriangle size={11} />
              affects everyone
            </span>
          )}
          <span className="tiny faint">saved {dateTime(row.updated_at)}</span>
        </div>
      </div>
      <textarea
        className="textarea config-editor"
        value={text}
        spellCheck={false}
        onChange={(event) => {
          setText(event.target.value);
          setSaved(false);
        }}
      />
      <div className="row-between">
        <span className="tiny" style={{ color: error ? "var(--danger)" : "var(--mint-ink)" }}>
          {error ?? (saved ? "Saved." : "")}
        </span>
        <button
          className="btn btn-ghost btn-sm"
          onClick={submit}
          disabled={mutation.isPending}
        >
          <Save size={12} />
          {mutation.isPending ? "Saving…" : "Save value"}
        </button>
      </div>
    </div>
  );
}

export function ConfigPage() {
  const config = useQuery({ queryKey: ["config"], queryFn: fetchConfig });

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Operational knobs</h2>
          <p>Client-safe values only. API keys live in Edge Function secrets, never here.</p>
        </div>
        <span className="badge badge-sky">
          <Settings2 size={12} />
          {config.data?.length ?? 0} keys
        </span>
      </div>

      <div className="banner banner-warn">
        <AlertTriangle size={16} />
        Changes are live for every client on its next refresh. Update limits carefully:
        the AI cap is the only thing standing between a free quota and a bill.
      </div>

      <section className="card">
        {config.isLoading && <div className="loading-block"><div className="spinner" /></div>}
        {config.error && (
          <div className="banner banner-danger">{(config.error as Error).message}</div>
        )}
        {(config.data ?? []).map((row) => (
          <ConfigRow key={row.key} row={row} />
        ))}
      </section>
    </>
  );
}
